/**
 * Creates a keyboard handler for canvas operations (undo, redo, delete, select all)
 * @param {Object} params - Parameters object
 * @param {Function} params.undo - Undo function
 * @param {Function} params.redo - Redo function
 * @param {Object} params.selectedObject - Currently selected object
 * @param {Function} params.deleteSelectedObject - Function to delete selected object
 * @param {Array} params.selectedObjects - Array of selected objects
 * @param {Function} params.setSelectedObjects - Function to set selected objects array
 * @param {Function} params.setIsMultiSelectMode - Function to toggle multi-select mode
 * @param {Function} params.deleteAllSelectedObjects - Function to delete all selected objects
 * @param {Object} params.stageRef - Main canvas stage ref
 * @param {Object} params.offCanvasStageRef - Off-canvas stage ref
 * @param {Array} params.texts - Main canvas texts
 * @param {Array} params.rectangles - Main canvas rectangles
 * @param {Array} params.circles - Main canvas circles
 * @param {Array} params.arrows - Main canvas arrows
 * @param {Array} params.lines - Main canvas lines
 * @param {Array} params.doubleSidedArrows - Main canvas double-sided arrows
 * @param {Array} params.images - Main canvas images
 * @param {Array} params.offCanvasTexts - Off-canvas texts
 * @param {Array} params.offCanvasRectangles - Off-canvas rectangles
 * @param {Array} params.offCanvasCircles - Off-canvas circles
 * @param {Array} params.offCanvasArrows - Off-canvas arrows
 * @param {Array} params.offCanvasLines - Off-canvas lines
 * @param {Array} params.offCanvasDoubleSidedArrows - Off-canvas double-sided arrows
 * @param {Array} params.offCanvasImages - Off-canvas images
 * @param {Function} params.nudgeSelectedElements - (dx, dy) => move selection by delta in stage pixels
 * @returns {Function} Keyboard event handler function
 */
export const createKeyboardHandler = ({
  undo,
  redo,
  selectedObject,
  deleteSelectedObject,
  selectedObjects,
  setSelectedObjects,
  setSelectedObject,
  setIsMultiSelectMode,
  deleteAllSelectedObjects,
  nudgeSelectedElements,
  stageRef,
  offCanvasStageRef,
  texts,
  rectangles,
  circles,
  arrows,
  lines,
  doubleSidedArrows,
  images,
  offCanvasTexts,
  offCanvasRectangles,
  offCanvasCircles,
  offCanvasArrows,
  offCanvasLines,
  offCanvasDoubleSidedArrows,
  offCanvasImages,
}) => {
  const getActiveScrollableParent = () => {
    const mainStage = stageRef?.current;
    const offStage = offCanvasStageRef?.current || window.__offCanvasStageRef?.current;
    if (!mainStage) return null;

    const mouseX =
      window.lastMouseX !== undefined ? window.lastMouseX : window.innerWidth / 2;
    const mouseY =
      window.lastMouseY !== undefined ? window.lastMouseY : window.innerHeight / 2;
    const elementUnderCursor = document.elementFromPoint(mouseX, mouseY);

    const getScrollParent = (node) => {
      let parent = node?.parentElement || null;
      while (parent) {
        const style = window.getComputedStyle(parent);
        const overflow = `${style.overflow}${style.overflowX}${style.overflowY}`;
        if (overflow.includes("auto") || overflow.includes("scroll")) {
          return parent;
        }
        parent = parent.parentElement;
      }
      return null;
    };

    const mainContainer = mainStage.container?.();
    const offContainer = offStage?.container?.();
    if (offContainer && elementUnderCursor && offContainer.contains(elementUnderCursor)) {
      return getScrollParent(offContainer) || getScrollParent(mainContainer);
    }
    if (mainContainer && elementUnderCursor && mainContainer.contains(elementUnderCursor)) {
      return getScrollParent(mainContainer);
    }
    return getScrollParent(mainContainer);
  };

  const panCanvasWithArrowKey = (key, step = 40) => {
    const scrollParent = getActiveScrollableParent();
    if (!scrollParent) return false;

    switch (key) {
      case "ArrowUp":
        scrollParent.scrollTop -= step;
        return true;
      case "ArrowDown":
        scrollParent.scrollTop += step;
        return true;
      case "ArrowLeft":
        scrollParent.scrollLeft -= step;
        return true;
      case "ArrowRight":
        scrollParent.scrollLeft += step;
        return true;
      default:
        return false;
    }
  };

  return (e) => {
    // Check if we're in an input field or contentEditable element
    const isInInput =
      e.target.tagName === "INPUT" ||
      e.target.tagName === "TEXTAREA" ||
      e.target.isContentEditable;

    if (isInInput) return; // Don't handle shortcuts in input fields

    if (e.ctrlKey || e.metaKey) {
      switch (e.key.toLowerCase()) {
        case "z":
          e.preventDefault();
          if (e.shiftKey) {
            // Ctrl+Shift+Z or Cmd+Shift+Z for redo
            redo();
          } else {
            // Ctrl+Z or Cmd+Z for undo
            undo();
          }
          break;
        case "y":
          e.preventDefault();
          // Ctrl+Y or Cmd+Y for redo (alternative)
          redo();
          break;
        case "a":
          e.preventDefault();
          // Ctrl+A or Cmd+A for select all
          handleSelectAll();
          break;
      }
    } else {
      // Handle single key presses
      switch (e.key) {
        case "Delete":
        case "Backspace":
          e.preventDefault();
          // Check if there are multiple selected objects
          const hasMultipleSelected = selectedObjects && selectedObjects.length > 0;
          const hasSelectedObject = selectedObject !== null && selectedObject !== undefined;

          if (hasMultipleSelected || hasSelectedObject) {
            // If we have multiple selected objects, use deleteAllSelectedObjects
            if (hasMultipleSelected && deleteAllSelectedObjects) {
              deleteAllSelectedObjects();
            } else if (hasSelectedObject && deleteSelectedObject) {
              // Otherwise, delete just the selectedObject
              deleteSelectedObject();
            }
          }
          break;
        case "ArrowUp":
        case "ArrowDown":
        case "ArrowLeft":
        case "ArrowRight":
          if (typeof nudgeSelectedElements === "function") {
            const hasMulti =
              selectedObjects && selectedObjects.length > 0;
            const hasSingle =
              selectedObject !== null && selectedObject !== undefined;
            if (hasMulti || hasSingle) {
              e.preventDefault();
              const step = e.shiftKey ? 10 : 1;
              let dx = 0;
              let dy = 0;
              switch (e.key) {
                case "ArrowUp":
                  dy = -step;
                  break;
                case "ArrowDown":
                  dy = step;
                  break;
                case "ArrowLeft":
                  dx = -step;
                  break;
                case "ArrowRight":
                  dx = step;
                  break;
                default:
                  break;
              }
              nudgeSelectedElements(dx, dy);
            } else if (panCanvasWithArrowKey(e.key, e.shiftKey ? 120 : 40)) {
              e.preventDefault();
            }
          }
          break;
      }
    }
  };

  /**
   * Handle Ctrl+A to select all elements based on which stage has focus
   */
  function handleSelectAll() {
    // Determine which stage the cursor is over by checking the active element or pointer position
    const mainStage = stageRef?.current;
    const offStage = offCanvasStageRef?.current || window.__offCanvasStageRef?.current;

    if (!mainStage) return;

    // Check if the pointer is over the off-canvas stage
    let isOverOffCanvas = false;

    if (offStage) {
      try {
        const mainCanvasContainer = mainStage.container();
        const offCanvasContainer = offStage.container();

        // Method 1: Check which container the element under cursor belongs to
        // Use stored mouse position or center of screen as fallback
        const mouseX = window.lastMouseX !== undefined ? window.lastMouseX : window.innerWidth / 2;
        const mouseY = window.lastMouseY !== undefined ? window.lastMouseY : window.innerHeight / 2;

        const elementUnderCursor = document.elementFromPoint(mouseX, mouseY);

        // Check if the element under cursor is within off-canvas container
        if (elementUnderCursor) {
          if (offCanvasContainer.contains(elementUnderCursor)) {
            isOverOffCanvas = true;
          } else if (mainCanvasContainer.contains(elementUnderCursor)) {
            isOverOffCanvas = false;
          } else {
            // Method 2: Check active element as fallback
            const activeElement = document.activeElement;

            if (activeElement) {
              if (offCanvasContainer.contains(activeElement)) {
                isOverOffCanvas = true;
              } else if (mainCanvasContainer.contains(activeElement)) {
                isOverOffCanvas = false;
              }
            }
          }
        }
      } catch (err) {
        // If we can't determine, default to main canvas
        isOverOffCanvas = false;
      }
    }

    const isMarkedOffCanvas = (el) =>
      !!(
        el?.isOffcanvas === true ||
        el?.isOffCanvas === true ||
        el?.is_off_canvas === true ||
        el?.shape_properties?.isOffcanvas === true ||
        el?.shape_properties?.isOffCanvas === true ||
        el?.shape_properties?.is_off_canvas === true ||
        el?.shape_info?.isOffcanvas === true ||
        el?.shape_info?.isOffCanvas === true ||
        el?.shape_info?.is_off_canvas === true ||
        el?.image_properties?.isOffcanvas === true ||
        el?.image_properties?.isOffCanvas === true ||
        el?.image_properties?.is_off_canvas === true
      );

    // Select all elements from the appropriate canvas
    if (isOverOffCanvas && offStage) {
      // Select all off-canvas elements
      const allOffCanvasElements = [
        ...(offCanvasTexts || []).map(el => ({ ...el, type: 'Text' })),
        ...(offCanvasRectangles || []).map(el => ({ ...el, type: 'Rect' })),
        ...(offCanvasCircles || []).map(el => ({ ...el, type: 'Circle' })),
        ...(offCanvasArrows || []).map(el => ({ ...el, type: 'Arrow' })),
        ...(offCanvasLines || []).map(el => ({ ...el, type: 'Line' })),
        ...(offCanvasDoubleSidedArrows || []).map(el => ({ ...el, type: 'DoubleSidedArrow' })),
        ...(offCanvasImages || []).map(el => ({ ...el, type: 'Image' })),
      ];

      const offOnlyElements = allOffCanvasElements;

      if (offOnlyElements.length > 0) {
        setSelectedObjects(offOnlyElements);
        if (typeof setSelectedObject === "function") {
          setSelectedObject(offOnlyElements[0] || null);
        }
        if (typeof setIsMultiSelectMode === "function") {
          setIsMultiSelectMode(offOnlyElements.length > 1);
        }
      } else if (typeof setSelectedObject === "function") {
        setSelectedObject(null);
      }
    } else {
      // Select all main canvas elements
      const allMainCanvasElements = [
        ...(texts || []).map(el => ({ ...el, type: 'Text' })),
        ...(rectangles || []).map(el => ({ ...el, type: 'Rect' })),
        ...(circles || []).map(el => ({ ...el, type: 'Circle' })),
        ...(arrows || []).map(el => ({ ...el, type: 'Arrow' })),
        ...(lines || []).map(el => ({ ...el, type: 'Line' })),
        ...(doubleSidedArrows || []).map(el => ({ ...el, type: 'DoubleSidedArrow' })),
        ...(images || []).map(el => ({ ...el, type: 'Image' })),
      ];

      // Some main arrays can transiently contain off-canvas-marked entries; exclude them.
      const mainOnlyElements = allMainCanvasElements.filter(
        (el) => !isMarkedOffCanvas(el),
      );

      if (mainOnlyElements.length > 0) {
        setSelectedObjects(mainOnlyElements);
        if (typeof setSelectedObject === "function") {
          setSelectedObject(mainOnlyElements[0] || null);
        }
        if (typeof setIsMultiSelectMode === "function") {
          setIsMultiSelectMode(mainOnlyElements.length > 1);
        }
      } else if (typeof setSelectedObject === "function") {
        setSelectedObject(null);
      }
    }
  }
};
